/**
 * SystemShell — domain frame for System / platform-wide config.
 *
 * Houses the global toggles, limits, and environment knobs that don't fit
 * cleanly under Identity / Org / Products. Each row here can shift behavior
 * for the entire bank — gated to SuperAdmin / Admin.
 *
 * v0.9.6 ships with Settings only. Future sub-tabs (KillList, SlaTargets,
 * CD Penalty Rules, etc.) plug into the same shell when those editors land.
 */

import { useAuth } from '@/contexts/AuthContext';
import { DomainShell } from '@/components/DomainShell';

const REQ = ['SuperAdmin', 'Admin'] as const;

const SUBNAV = [
  { slug: 'critical-settings', label: 'Critical Settings', requiredRoles: REQ },
  { slug: 'account-maintenance', label: 'Account Maintenance', requiredRoles: REQ },
  { slug: 'settings', label: 'Settings', requiredRoles: REQ },
  { slug: 'sla-targets', label: 'SLA Targets', requiredRoles: REQ },
  { slug: 'jxchange-diagnostics', label: 'jXchange Diagnostics', requiredRoles: REQ },
  // Migrated from ROP-teams Admin 2026-06-16 (BACKLOG-0579/0101, parallel dispatch).
  { slug: 'kill-list', label: 'Kill List', requiredRoles: REQ },
  { slug: 'pilot-officers', label: 'Pilot Officers', requiredRoles: REQ },
  { slug: 'cart-retention-policies', label: 'Cart Retention', requiredRoles: REQ },
  { slug: 'retention-policies', label: 'Retention Policies', requiredRoles: REQ },
  { slug: 'alert-thresholds', label: 'Alert Thresholds', requiredRoles: REQ },
  { slug: 'rcp', label: 'RCP (Phase 2)', requiredRoles: REQ },
  { slug: 'purge-reason-codes', label: 'Purge Reason Codes', requiredRoles: REQ },
];

export function SystemShell(): JSX.Element {
  const { user } = useAuth();
  return (
    <DomainShell
      title="System"
      description="Bank-wide platform settings. A single row in Settings can flip behavior for every cart, every officer, every customer. Make changes deliberately — there's no per-user override here."
      basePath="/retail/system"
      items={SUBNAV.map((s) => ({
        slug: s.slug,
        label: s.label,
        requiredRoles: s.requiredRoles,
      }))}
      userRoles={user?.roles ?? []}
    />
  );
}
